import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Typography, Divider, Tag } from 'antd';
import {
  DatabaseOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { DataCard } from '../common';
import { useTenant } from '../../context/TenantContext';
import { isM365Managed } from '../../utils/customerKey';
import {
  useDattoIdFromCipp,
  useDattoDevices,
  useDattoAlerts,
  useDattoEndpointAgents,
  useDattoProtectedAssets,
} from '../../hooks';
import {
  parseMCPResponse,
} from '../../utils/dattoHelpers';

const { Text } = Typography;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid var(--odin-border);

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled(Text)`
  font-size: 14px;
  color: var(--odin-text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $status, theme }) => {
    if ($status === 'critical') return theme.colors.error;
    if ($status === 'warning') return theme.colors.warning;
    return theme.colors.accent;
  }};
`;

const DeviceList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DeviceItem = styled.div`
  padding: 12px;
  background: var(--odin-surface-hover);
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid var(--odin-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--odin-surface-hover);
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const DeviceName = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const DeviceStatus = styled.div`
  font-size: 13px;
  color: ${({ $online, theme }) => ($online ? theme.colors.success : theme.colors.error)};
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 ${({ theme }) => theme.spacing.sm};
`;

const DeviceSerial = styled(Text)`
  font-size: 12px;
  color: var(--odin-text-tertiary);
  font-family: ${({ theme }) => theme.typography.fontFamily.secondary};
`;

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-status-awaiting-border);
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: var(--odin-status-awaiting-text);
  font-size: 12px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyStateMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  min-height: 120px;
  text-align: center;
`;

/**
 * DattoWidget - Displays Datto BCDR backup data
 *
 * This component integrates with Datto-MCP to display:
 * - BCDR device count and online status
 * - Protected systems count (VMs, Physical servers, FileShares)
 * - PC endpoint agent count and health status
 * - Active alerts (critical and warning breakdown)
 * - List of devices with serial numbers
 * - List of endpoint backup agents with status
 *
 * Uses customerMappingService (via useDattoIdFromCipp) to resolve
 * CIPP tenant IDs to Datto organization IDs.
 *
 * @param {Object} props
 * @param {Function} props.onRefresh - Refresh callback
 */
const DattoWidget = ({ onRefresh }) => {
  // Get selected tenant from context (CIPP tenant ID)
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Datto client name using customer mapping service
  const {
    dattoClient,
    isResolved,
    isLoading: isMappingLoading,
    customer,
  } = useDattoIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId,
  });

  const hasDattoMapping = isResolved;

  // Fetch Datto devices data
  const {
    data: devicesData,
    isLoading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useDattoDevices(dattoClient, {
    enabled: !!dattoClient,
  });

  // Fetch Datto alerts
  const {
    data: alertsData,
    isLoading: alertsLoading,
    error: alertsError,
    refetch: refetchAlerts,
  } = useDattoAlerts(dattoClient, {
    enabled: !!dattoClient,
  });

  // Fetch Datto endpoint agents (PC backup agents)
  const {
    data: agentsData,
    isLoading: agentsLoading,
    error: agentsError,
    refetch: refetchAgents,
  } = useDattoEndpointAgents(dattoClient, {
    enabled: !!dattoClient,
  });

  // Fetch Datto protected assets (VMs, Physical servers, FileShares)
  const {
    data: assetsData,
    isLoading: assetsLoading,
    error: assetsError,
    refetch: refetchAssets,
  } = useDattoProtectedAssets(dattoClient, 'all', {
    enabled: !!dattoClient,
  });

  // Calculate metrics from Datto data
  const metrics = useMemo(() => {
    // Parse devices from Datto-MCP response using defensive parser
    const parsedDevices = parseMCPResponse(devicesData);
    const parsedAlerts = parseMCPResponse(alertsData);
    const parsedAgents = parseMCPResponse(agentsData);
    const parsedAssets = parseMCPResponse(assetsData);

    // Extract arrays with type validation
    const devices = Array.isArray(parsedDevices?.devices)
      ? parsedDevices.devices
      : Array.isArray(parsedDevices?.data?.devices)
        ? parsedDevices.data.devices
        : [];

    const alerts = Array.isArray(parsedAlerts?.alerts)
      ? parsedAlerts.alerts
      : Array.isArray(parsedAlerts?.data?.alerts)
        ? parsedAlerts.data.alerts
        : [];

    const agents = Array.isArray(parsedAgents?.agents)
      ? parsedAgents.agents
      : Array.isArray(parsedAgents?.data?.agents)
        ? parsedAgents.data.agents
        : [];

    const allAssets = Array.isArray(parsedAssets?.assets)
      ? parsedAssets.assets
      : Array.isArray(parsedAssets?.data?.assets)
        ? parsedAssets.data.assets
        : [];

    // Filter to BCDR assets only (exclude SaaS)
    // Include: VM, Physical, FileShare, Cloud, and agent (agent-based server backups)
    const bcdrAssets = allAssets.filter(a =>
      a.type !== 'SaaS' && ['VM', 'Physical', 'FileShare', 'Cloud', 'agent'].includes(a.type)
    );

    // Calculate metrics with safe array operations
    const onlineDevices = devices.filter(d =>
      d.status === 'online' || d.status === 'Online'
    ).length;

    const criticalAlerts = alerts.filter(a =>
      a.severity === 'critical' || a.severity === 'Critical'
    ).length;

    const warningAlerts = alerts.filter(a =>
      a.severity === 'warning' || a.severity === 'Warning'
    ).length;

    const healthyAgents = agents.filter(a => a.status === 'healthy').length;
    const warningAgentCount = agents.filter(a => a.status === 'warning').length;

    const protectedAssets = bcdrAssets.filter(a => a.status === 'protected').length;
    const unprotectedAssets = bcdrAssets.filter(a => a.status === 'unprotected').length;
    const warningAssets = bcdrAssets.filter(a => a.status === 'warning').length;

    return {
      devices,
      totalDevices: devices.length,
      onlineDevices,
      offlineDevices: devices.length - onlineDevices,
      alerts,
      totalAlerts: alerts.length,
      criticalAlerts,
      warningAlerts,
      agents,
      totalAgents: agents.length,
      healthyAgents,
      warningAgentCount,
      assets: bcdrAssets,
      totalAssets: bcdrAssets.length,
      protectedAssets,
      unprotectedAssets,
      warningAssets,
    };
  }, [devicesData, alertsData, agentsData, assetsData]);

  const isLoading = isMappingLoading || devicesLoading || alertsLoading || agentsLoading || assetsLoading;
  const error = devicesError || alertsError || agentsError || assetsError;
  const showMappingWarning = !isLoading && !hasDattoMapping && cippTenantId;

  // Calculate empty state - only when mapped but no data
  const showEmpty = hasDattoMapping && !metrics.totalDevices && !metrics.totalAlerts && !metrics.totalAgents && !metrics.totalAssets && !isLoading;

  // Handle refresh
  const handleRefresh = () => {
    if (dattoClient) {
      refetchDevices();
      refetchAlerts();
      refetchAgents();
      refetchAssets();
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <DataCard
      title="Datto BCDR"
      icon={DatabaseOutlined}
      loading={isLoading}
      error={error}
      empty={false}
      onRefresh={handleRefresh}
    >
      {/* Show mapping warning ALWAYS when applicable */}
      {showMappingWarning && (
        <MappingWarning>
          <WarningOutlined />
          {isM365Managed(selectedTenant)
            ? 'No Datto mapping found for this tenant. Click "Sync Platforms" to discover.'
            : 'No Datto mapping found for this customer. Add it in Admin → Customer Mappings.'}
        </MappingWarning>
      )}

      {/* Show data content ONLY when mapped AND has data */}
      {hasDattoMapping && !showEmpty && (
        <>
          {/* Device Status */}
          <MetricRow>
            <MetricLabel>
              <DatabaseOutlined />
              Devices
            </MetricLabel>
            <MetricValue>
              {metrics.onlineDevices}/{metrics.totalDevices} online
            </MetricValue>
          </MetricRow>

          {/* Alerts Summary */}
          <MetricRow>
            <MetricLabel>
              <ExclamationCircleOutlined />
              Active Alerts
            </MetricLabel>
            <MetricValue $status={metrics.criticalAlerts > 0 ? 'critical' : 'normal'}>
              {metrics.totalAlerts} total
              {metrics.criticalAlerts > 0 && ` (${metrics.criticalAlerts} critical)`}
            </MetricValue>
          </MetricRow>

          {/* Endpoint Agents Summary */}
          {metrics.totalAgents > 0 && (
            <MetricRow>
              <MetricLabel>
                <CheckCircleOutlined />
                PC Agents
              </MetricLabel>
              <MetricValue $status={metrics.warningAgentCount > 0 ? 'warning' : 'normal'}>
                {metrics.healthyAgents}/{metrics.totalAgents} healthy
              </MetricValue>
            </MetricRow>
          )}

          {/* Protected Assets Summary */}
          {metrics.totalAssets > 0 && (
            <MetricRow>
              <MetricLabel>
                <DatabaseOutlined />
                Protected Systems
              </MetricLabel>
              <MetricValue $status={metrics.unprotectedAssets > 0 || metrics.warningAssets > 0 ? 'warning' : 'normal'}>
                {metrics.protectedAssets}/{metrics.totalAssets} protected
              </MetricValue>
            </MetricRow>
          )}

          {/* Device List */}
          {metrics.devices.length > 0 && (
            <DeviceList>
              {metrics.devices.slice(0, 5).map(device => {
                const isOnline = device.status === 'online' || device.status === 'Online';
                return (
                  <DeviceItem key={device.serialNumber || device.id}>
                    <DeviceName>{device.name || device.deviceName || 'Unknown Device'}</DeviceName>
                    <DeviceStatus $online={isOnline}>
                      {isOnline ? (
                        <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
                      ) : (
                        <ExclamationCircleOutlined style={{ color: 'var(--odin-error)' }} />
                      )}
                      {device.status || 'Unknown'}
                    </DeviceStatus>
                    <DeviceSerial>{device.serialNumber || device.serial || 'N/A'}</DeviceSerial>
                  </DeviceItem>
                );
              })}
              {metrics.devices.length > 5 && (
                <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                  + {metrics.devices.length - 5} more devices
                </Text>
              )}
            </DeviceList>
          )}

          {/* Protected Assets List (Server Nodes/VMs) */}
          {metrics.assets.length > 0 && (
            <>
              {(metrics.devices.length > 0 || metrics.agents.length > 0) && <Divider style={{ margin: '16px 0', borderColor: 'var(--odin-border)' }} />}
              <DeviceList>
                {metrics.assets.slice(0, 5).map(asset => {
                  const isProtected = asset.status === 'protected';
                  const isWarning = asset.status === 'warning';
                  return (
                    <DeviceItem key={asset.id || asset.name}>
                      <DeviceName>{asset.name || 'Unknown System'}</DeviceName>
                      <Tag color={asset.type === 'VM' ? 'blue' : asset.type === 'Physical' ? 'green' : 'purple'} style={{ margin: '0 8px', fontSize: 11 }}>
                        {asset.type || 'Unknown'}
                      </Tag>
                      <DeviceStatus $online={isProtected}>
                        {isProtected ? (
                          <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
                        ) : isWarning ? (
                          <WarningOutlined style={{ color: 'var(--odin-warning)' }} />
                        ) : (
                          <ExclamationCircleOutlined style={{ color: 'var(--odin-error)' }} />
                        )}
                        {asset.status || 'Unknown'}
                      </DeviceStatus>
                    </DeviceItem>
                  );
                })}
                {metrics.assets.length > 5 && (
                  <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                    + {metrics.assets.length - 5} more systems
                  </Text>
                )}
              </DeviceList>
            </>
          )}

          {/* Endpoint Agent List */}
          {metrics.agents.length > 0 && (
            <>
              {(metrics.devices.length > 0 || metrics.assets.length > 0) && <Divider style={{ margin: '16px 0', borderColor: 'var(--odin-border)' }} />}
              <DeviceList>
                {metrics.agents.slice(0, 3).map(agent => {
                  const isHealthy = agent.status === 'healthy';
                  return (
                    <DeviceItem key={agent.uuid}>
                      <DeviceName>{agent.hostname || 'Unknown PC'}</DeviceName>
                      <DeviceStatus $online={isHealthy}>
                        {isHealthy ? (
                          <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
                        ) : agent.status === 'warning' ? (
                          <WarningOutlined style={{ color: 'var(--odin-warning)' }} />
                        ) : (
                          <ExclamationCircleOutlined style={{ color: 'var(--odin-error)' }} />
                        )}
                        {agent.status || 'Unknown'}
                      </DeviceStatus>
                      <DeviceSerial>{agent.shortCode || 'N/A'}</DeviceSerial>
                    </DeviceItem>
                  );
                })}
                {metrics.agents.length > 3 && (
                  <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                    + {metrics.agents.length - 3} more agents
                  </Text>
                )}
              </DeviceList>
            </>
          )}
        </>
      )}

      {/* Show placeholder when mapped but no data yet */}
      {hasDattoMapping && showEmpty && !showMappingWarning && (
        <EmptyStateMessage>
          <DatabaseOutlined style={{ fontSize: 48, color: 'var(--odin-text-tertiary)' }} />
          <Text style={{ color: 'var(--odin-text-tertiary)', marginTop: 16 }}>
            No Datto BCDR devices, protected systems, or endpoint agents found for this tenant
          </Text>
        </EmptyStateMessage>
      )}
    </DataCard>
  );
};

DattoWidget.propTypes = {
  onRefresh: PropTypes.func,
};

export default DattoWidget;
